"use client";

import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Info, Plus, Upload, Loader2, ShieldAlert } from "lucide-react";
import axios from "axios";
// Axios is already imported at line 22 based on previous reads, but verifying uniqueness if needed.
// The file view showing line 22: import axios from "axios";
// So I don't need to add it again.
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  getBrands,
  addBrand,
  reset as resetBrand,
} from "@/state/brand/brand.slice";
import { getCategories } from "@/state/category/category.slice";
import {
  getUnits,
  addUnit,
  reset as resetUnit,
} from "@/state/units/unit.slice";
import { getShops } from "@/state/shops/shop.slice";
import { addProduct, reset as resetProduct } from "@/state/products/product.slice";
import PermissionGuard from "@/components/custom/common/PermissionGuard";

const schema = z.object({
  productName: z.string().min(1, "Product name is required"),
  sku: z.string().min(1, "SKU is required"),
  barcode: z.string().optional(),
  barcodeType: z.string().min(1, "Barcode type is required"),
  unit: z.string().min(1, "Unit is required"),
  brand: z.string().optional(),
  category: z.string().min(1, "Category is required"),
  subCategory: z.string().optional(),
  shop: z.string().optional(),
  alertQuantity: z.number().min(0, "Alert quantity must be 0 or greater"),
  manageStock: z.boolean(),
  quantity: z.number().optional(),
  buying_price: z.number().min(0, "Buying price must be 0 or greater"),
  current_price: z.number().min(0, "Retail price must be 0 or greater"),
  wholesale_price: z.number().min(0, "Wholesale price must be 0 or greater"),
  description: z.string().optional(),
  productImage: z.instanceof(File).optional(),
  productBrochure: z.instanceof(File).optional(),
});

export default function AddProductForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showStockModal, setShowStockModal] = useState(false);
  const [showBrandDrawer, setShowBrandDrawer] = useState(false);
  const [showUnitDrawer, setShowUnitDrawer] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const {
    brands,
    loading: brandLoading,
    success: brandSuccess,
  } = useSelector((state) => state.brand);
  const { categories } = useSelector((state) => state.category);
  const {
    units,
    loading: unitLoading,
    success: unitSuccess,
  } = useSelector((state) => state.unit);
  const { shops } = useSelector((state) => state.shop || { shops: [] });
  const { success, isLoading } = useSelector((state) => state.product);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (brandSuccess) {
      toast({
        title: "Success",
        description: "Brand added successfully!",
      });
      setShowBrandDrawer(false);
      dispatch(resetBrand());
      dispatch(getBrands());
    }
    if (unitSuccess) {
      toast({
        title: "Success",
        description: "Unit added successfully!",
      });
      setShowUnitDrawer(false);
      dispatch(resetUnit());
      dispatch(getUnits());
    }
  }, [brandSuccess, unitSuccess, toast, setShowBrandDrawer, setShowUnitDrawer]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      productName: "",
      sku: "",
      barcode: "",
      barcodeType: "code128",  // Default to Code 128
      unit: "21",              // Default unit ID
      brand: "25",             // Default brand ID
      category: "26",          // Default category ID
      subCategory: "",
      shop: "",
      manageStock: false,
      alertQuantity: 0,
      quantity: 0,
      buying_price: 0,
      current_price: 0,
      wholesale_price: 0,
      description: "",
      supplier: "",
    },
  });


  const manageStock = watch("manageStock");



  useEffect(() => {
    dispatch(getBrands());
    dispatch(getCategories());
    dispatch(getUnits());
    dispatch(getShops());

    const fetchSuppliers = async () => {
      try {
        const res = await axios.get(`${import.meta.env.VITE_API_URL}suppliers`, { withCredentials: true });
        if (res.data.success) {
          setSuppliers(res.data.data);
        }
      } catch (e) { console.error("Failed to load suppliers"); }
    };
    fetchSuppliers();
  }, [dispatch]);

  // Auto-set shop from user's assigned shop
  useEffect(() => {
    if (user?.shop_id) {
      setValue("shop", user.shop_id.toString());
    }
  }, [user, setValue]);

  useEffect(() => {
    if (manageStock) {
      setShowStockModal(true);
    }
  }, [manageStock]);

  useEffect(() => {
    if (success) {
      toast({
        title: "Success",
        description: "Product added successfully!",
      });
      reset(); // Clear the form on successful submit
      dispatch(resetProduct()); // Reset the product slice state
    }
  }, [success, toast, reset, dispatch]);

  const onSubmit = async (data) => {
    // Create native FormData payload to intercept File uploads and Text natively
    const payload = new FormData();
    
    // Map text data
    const mappedData = {
      ...data,
      brand_id: data.brand || null,
      category_id: data.category || null,
      unit_id: data.unit || null,
      shop_id: data.shop || null,
      buying_price: Number(data.buying_price),
      current_price: Number(data.current_price),
      wholesale_price: Number(data.wholesale_price),
      quantity: Number(data.quantity),
      alertQuantity: Number(data.alertQuantity),
      supplier: data.supplier || null,
    };

    // Remove keys that were replaced
    delete mappedData.brand;
    delete mappedData.category;
    delete mappedData.unit;
    delete mappedData.shop;
    delete mappedData.productImage;
    delete mappedData.productBrochure;

    // Append standard generic fields
    Object.keys(mappedData).forEach(key => {
      if (mappedData[key] !== null && mappedData[key] !== undefined) {
         payload.append(key, mappedData[key]);
      }
    });

    // Append File instances if they exist
    if (data.productImage instanceof File) {
      payload.append('productImage', data.productImage);
    }
    if (data.productBrochure instanceof File) {
      payload.append('productBrochure', data.productBrochure);
    }

    dispatch(addProduct(payload));
  };

  const handleAddBrand = async (data) => {
    dispatch(addBrand(data));
  };

  const handleAddUnit = async (data) => {
    dispatch(addUnit(data));
  };

  const handleStockManagement = (quantity) => {
    setValue("quantity", quantity);
    setShowStockModal(false);
  };

  // Access denied fallback component
  const AccessDenied = (
    <div className="mx-auto p-4 max-w-2xl">
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <ShieldAlert className="h-16 w-16 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-600 dark:text-red-400">
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            You don't have permission to add new products.
          </p>
          <p className="text-sm text-muted-foreground">
            The <strong>"Add Products"</strong> permission is required to access this page.
            Please contact your administrator to request access.
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <PermissionGuard required="AddProducts" fallback={AccessDenied}>
      <form onSubmit={handleSubmit(onSubmit)} className="mx-auto p-4">
        <h1 className="text-xl text-primary font-bold mb-8">Add New Product</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Product Name */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Product Name</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="productName"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter product name" />
              )}
            />
            {errors.productName && (
              <p className="text-red-500 text-sm mt-1">
                {errors.productName.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* SKU */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-sm">
              SKU
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm bg-gray-50 dark:bg-slate-8000 text-white p-4">
                    The Stock Keeping Unit (SKU) is <br /> a unique identifier
                    for each product. It is used to track <br /> inventory
                    levels and manage
                    <br /> product availability.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="sku"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Enter SKU" />
              )}
            />
            {errors.sku && (
              <p className="text-red-500 text-sm mt-1">{errors.sku.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Barcode */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-sm">
              Barcode
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm bg-gray-50 dark:bg-slate-8000 text-white p-4">
                    Enter the barcode number for scanner integration. <br />
                    This allows fast product lookup during sales. <br />
                    Leave empty if not using barcode scanners.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="barcode"
              control={control}
              render={({ field }) => (
                <Input {...field} placeholder="Scan or enter barcode (optional)" />
              )}
            />
            {errors.barcode && (
              <p className="text-red-500 text-sm mt-1">{errors.barcode.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Barcode Type */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Barcode Type</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="barcodeType"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select barcode type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="code128">Code 128 (C128)</SelectItem>
                    <SelectItem value="ean13">EAN-13</SelectItem>
                    <SelectItem value="upc">UPC</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.barcodeType && (
              <p className="text-red-500 text-sm mt-1">
                {errors.barcodeType.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Unit */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Unit</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units?.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id?.toString()}>
                        {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.unit && (
              <p className="text-red-500 text-sm mt-1">{errors.unit.message}</p>
            )}
            <Drawer open={showUnitDrawer} onOpenChange={setShowUnitDrawer}>
              <DrawerTrigger asChild>
                <Button variant="outline" size="sm" className="mt-2">
                  <Plus className="mr-2 h-4 w-4" /> Add New Unit
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>Add New Unit</DrawerTitle>
                </DrawerHeader>
                <div className="p-4">
                  <AddUnitForm loading={unitLoading} onSubmit={handleAddUnit} />
                </div>
              </DrawerContent>
            </Drawer>
          </CardContent>
        </Card>

        {/* Brand */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Brand</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="brand"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands?.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id?.toString()}>
                        {brand.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <Drawer open={showBrandDrawer} onOpenChange={setShowBrandDrawer}>
              <DrawerTrigger asChild>
                <Button variant="outline" size="sm" className="mt-2">
                  <Plus className="mr-2 h-4 w-4" /> Add New Brand
                </Button>
              </DrawerTrigger>
              <DrawerContent>
                <DrawerHeader>
                  <DrawerTitle>Add New Brand</DrawerTitle>
                </DrawerHeader>
                <div className="p-4">
                  <AddBrandForm
                    loading={brandLoading}
                    onSubmit={handleAddBrand}
                  />
                </div>
              </DrawerContent>
            </Drawer>
          </CardContent>
        </Card>

        {/* Shop */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Shop</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="shop"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select shop" />
                  </SelectTrigger>
                  <SelectContent>
                    {shops?.map((shop) => (
                      <SelectItem key={shop.id} value={shop.id?.toString()}>
                        {shop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </CardContent>
        </Card>

        {/* Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Category</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.id} value={category.id?.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">
                {errors.category.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Business Locations - Commented out as requested */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-sm">
              Business Locations (Optional)
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                    Select the business locations where this product will be
                    available. This field is optional.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="businessLocations"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={(value) => {
                    const updatedLocations = field.value.includes(value)
                      ? field.value.filter((loc) => loc !== value)
                      : [...field.value, value];
                    field.onChange(updatedLocations);
                  }}
                  value={field.value}
                  multiple
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select locations (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="location1">Location 1</SelectItem>
                    <SelectItem value="location2">Location 2</SelectItem>
                    <SelectItem value="location3">Location 3</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {control._formValues.businessLocations.map((location) => (
                <span
                  key={location}
                  className="inline-block bg-gray-200 rounded-full px-3 py-1 text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {location}
                </span>
              ))}
            </div>
          </CardContent>
        </Card> */}

        {/* Alert Quantity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-sm">
              Alert Quantity
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm bg-gray-50 dark:bg-slate-8000 text-white p-4">
                    Set the quantity at which you want to be alerted for low
                    stock.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="alertQuantity"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === "" ? "" : Number(value));
                  }}
                  value={field.value === 0 ? "" : field.value}
                  placeholder="Enter alert quantity"
                />
              )}
            />
            {errors.alertQuantity && (
              <p className="text-red-500 text-sm mt-1">
                {errors.alertQuantity.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Manage Stock */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-sm">
              Manage Stock
              <Tooltip>
                <TooltipTrigger>
                  <Info className="ml-2 h-4 w-4 text-gray-500 dark:text-gray-400 dark:text-gray-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm bg-gray-50 dark:bg-slate-8000 text-white p-4">
                    Enable this to manage stock <br /> for this product at the
                    product level.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Controller
                name="manageStock"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="manage-stock"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (checked) {
                        setShowStockModal(true);
                      }
                    }}
                  />
                )}
              />
              <Label htmlFor="manage-stock">
                Enable stock management at product level
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Buying Price */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Buying Price (GHS)</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="buying_price"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  value={field.value === 0 ? "" : field.value}
                  placeholder="Enter buying price"
                />
              )}
            />
            {errors.buying_price && (
              <p className="text-red-500 text-sm mt-1">
                {errors.buying_price.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Retail Price */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Retail Price (GHS)</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="current_price"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  value={field.value === 0 ? "" : field.value}
                  placeholder="Enter retail price"
                />
              )}
            />
            {errors.current_price && (
              <p className="text-red-500 text-sm mt-1">
                {errors.current_price.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Wholesale Price */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Wholesale Price (GHS)</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="wholesale_price"
              control={control}
              render={({ field }) => (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  value={field.value === 0 ? "" : field.value}
                  placeholder="Enter wholesale price"
                />
              )}
            />
            {errors.wholesale_price && (
              <p className="text-red-500 text-sm mt-1">
                {errors.wholesale_price.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Product Description */}
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle className="text-sm">Product Description</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder="Enter product description"
                  className="min-h-[200px]"
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Product Image */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Product Image</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center w-full">
              <Controller
                name="productImage"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <label
                    htmlFor="product-image"
                    className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 dark:border-slate-600 border-dashed cursor-pointer bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:bg-slate-800"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-10 h-10 mb-3 text-gray-400 dark:text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                        <span className="font-semibold">Click to upload</span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                        PNG, JPG or GIF (MAX. 800x400px)
                      </p>
                    </div>
                    <input
                      id="product-image"
                      type="file"
                      className="hidden"
                      onChange={(e) => onChange(e.target.files[0])}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Product Brochure */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Product Brochure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center w-full">
              <Controller
                name="productBrochure"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <label
                    htmlFor="product-brochure"
                    className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 dark:border-slate-600 border-dashed cursor-pointer bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:bg-slate-800"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-10 h-10 mb-3 text-gray-400 dark:text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                        <span className="font-semibold">Click to upload</span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                        PDF, CSV, ZIP, DOC, DOCX (MAX. 5MB)
                      </p>
                    </div>
                    <input
                      id="product-brochure"
                      type="file"
                      className="hidden"
                      onChange={(e) => onChange(e.target.files[0])}
                      {...field}
                    />
                  </label>
                )}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Submit Button */}
      <div className="mt-8 flex justify-end">
        <Button type="submit" size="lg" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding Product...
            </>
          ) : (
            "Add Product"
          )}
        </Button>
      </div>




      {/* Stock Management Modal */}
      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Stock</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="quantity" className="text-right">
                Quantity
              </Label>
              <Input
                id="quantity"
                type="number"
                className="col-span-3"
                defaultValue={watch("quantity")}
                onChange={(e) => setValue("quantity", Number(e.target.value))}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="supplier" className="text-right">
                Supplier
              </Label>
              <Select onValueChange={(val) => setValue("supplier", val)}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select Supplier (Optional)" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={() => setShowStockModal(false)}>
            Save
          </Button>
        </DialogContent>
      </Dialog>
      </form>
    </PermissionGuard>
  );
}

function AddBrandForm({ loading, onSubmit }) {
  const { register, handleSubmit } = useForm();

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div>
        <Label htmlFor="name">Brand Name</Label>
        <Input id="name" {...register("name", { required: true })} />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" {...register("description")} />
      </div>
      <Button disabled={loading} type="submit">
        {loading ? "Adding..." : "Add Brand"}
      </Button>
    </form>
  );
}

function AddUnitForm({ onSubmit, loading }) {
  const { register, handleSubmit } = useForm();

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div>
        <Label htmlFor="name">Unit Name</Label>
        <Input id="name" {...register("name", { required: true })} />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" {...register("description")} />
      </div>
      <Button disabled={loading} type="submit">
        {loading ? "Adding..." : "Add Unit"}
      </Button>
    </form>
  );
}
